import {
  IQueryToolAdminProject,
  IQueryToolAdminSubscriber,
  QueryToolSetting,
} from '../../../models/api/query-tool';
import { getProjectDisplayName } from '../../../scripts/project-name-utils';

/**
 * The CENTERLINE AI page's list logic, kept apart from the page so it can be tested
 * (tests/centerline-ai-admin-utils.test.ts).
 */

export const SETTINGS: QueryToolSetting[] = ['ingest', 'chat'];

export const SETTING_LABELS: Record<QueryToolSetting, string> = {
  ingest: 'Ingestion',
  chat: 'Chat',
};

/** Projects by their settings: Ingestion on, Chat on (either may be on too), or both off. */
export type ProjectFilter = 'all' | QueryToolSetting | 'off';

export const FILTER_LABELS: Record<ProjectFilter, string> = {
  all: 'All',
  ingest: 'Ingesting',
  chat: 'Chat on',
  off: 'Off',
};

const byName = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** By name as people read it: "Project 2" before "Project 10". */
export const sortByName = <T extends { name: string; id: string }>(list: T[]): T[] =>
  [...list].sort((x, y) => byName.compare(x.name, y.name) || (x.id < y.id ? -1 : 1));

const matches = (name: string, search: string) =>
  name.toLowerCase().includes(search.trim().toLowerCase());

/** Archived and deleted projects can't use CENTERLINE AI; they're listed only on request. */
export const isInactive = (p: IQueryToolAdminProject) => p.isArchived || p.isHidden;

/** Neither setting on. */
export const isOff = (p: IQueryToolAdminProject) => !p.ingest && !p.chat;

/** The projects the list shows before the settings filter: by name, and active unless asked. */
export function searchProjects(
  projects: IQueryToolAdminProject[],
  search: string,
  showInactive: boolean,
): IQueryToolAdminProject[] {
  return projects.filter(
    (p) => (showInactive || !isInactive(p)) && matches(getProjectDisplayName(p), search),
  );
}

const inFilter = (p: IQueryToolAdminProject, filter: ProjectFilter) => {
  if (filter === 'all') return true;
  if (filter === 'off') return isOff(p);
  return p[filter];
};

export function countByFilter(projects: IQueryToolAdminProject[]): Record<ProjectFilter, number> {
  const count = (f: ProjectFilter) => projects.filter((p) => inFilter(p, f)).length;
  return { all: projects.length, ingest: count('ingest'), chat: count('chat'), off: count('off') };
}

export const filterProjects = (projects: IQueryToolAdminProject[], filter: ProjectFilter) =>
  projects.filter((p) => inFilter(p, filter));

/**
 * Why `setting` can't be switched to `on` for a project right now, or null when it can. Switching
 * off is never blocked. The backend enforces the same rules (query-tool-admin-service.ts
 * setProjectSettings); this keeps the controls honest.
 */
export function blockedReason(
  project: IQueryToolAdminProject,
  setting: QueryToolSetting,
  on: boolean,
  entitled: boolean,
): string | null {
  if (project[setting] === on || !on) return null;
  if (!entitled) return 'Turn on CENTERLINE AI access for this subscriber first.';
  if (project.isHidden) return 'Deleted projects can’t use CENTERLINE AI.';
  if (project.isArchived) return 'Archived projects can’t use CENTERLINE AI.';
  return null;
}

/** For a bulk change: which of `projects` would change, and which are skipped and why. */
export function planBulkChange(
  projects: IQueryToolAdminProject[],
  setting: QueryToolSetting,
  on: boolean,
  entitled: boolean,
): { change: IQueryToolAdminProject[]; unchanged: number; skipped: number } {
  let unchanged = 0;
  let skipped = 0;
  const change: IQueryToolAdminProject[] = [];
  projects.forEach((p) => {
    if (p[setting] === on) unchanged += 1;
    else if (blockedReason(p, setting, on, entitled)) skipped += 1;
    else change.push(p);
  });
  return { change, unchanged, skipped };
}

export type SubscriberFilter = 'all' | 'access';

export function searchSubscribers(
  subscribers: IQueryToolAdminSubscriber[],
  search: string,
  filter: SubscriberFilter,
): IQueryToolAdminSubscriber[] {
  return subscribers.filter((s) => (filter === 'all' || s.entitled) && matches(s.name, search));
}

/** "2 ingesting · 1 chat · 248 projects" */
export function subscriberSummary(s: IQueryToolAdminSubscriber): string {
  const parts: string[] = [];
  if (s.ingesting) parts.push(`${s.ingesting} ingesting`);
  if (s.chat) parts.push(`${s.chat} chat`);
  parts.push(`${s.projects} ${s.projects === 1 ? 'project' : 'projects'}`);
  return parts.join(' · ');
}
